import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Eye, Loader2 } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

interface WatchlistCreateModalProps {
  open: boolean;
  onClose: () => void;
  workOrderId: number;
  workOrder?: any;
}

const AUTHORITY_TYPES = [
  { code: "BHO", label: "BHO - Broker, arranging for transportation of household goods" },
  { code: "BPH", label: "BPH - Broker in arranging for transportation of freight (including household goods)" },
  { code: "BPO", label: "BPO - Broker, arranging for transportation of freight (except household goods)" },
  { code: "CHO", label: "CHO - Common carrier of household goods" },
  { code: "CMH", label: "CMH - Common carrier of property, including household goods" },
  { code: "CMO", label: "CMO - Common carrier of property (except household goods)" },
  { code: "CPA", label: "CPA - Common carrier of passengers by motor vehicle" },
  { code: "CPC", label: "CPC - Common carrier of passengers, in charter and special operations" },
  { code: "CPN", label: "CPN - Common carrier of passengers (nonrecipient of government)" },
  { code: "CPR", label: "CPR - Common carrier of passengers" },
  { code: "CX2", label: "CX2 - Certificate of Registration" },
  { code: "ENO", label: "ENO - US based enterprise owned/controlled by persons of Mexico" },
  { code: "NC/A", label: "NC/A - Name change / Reentitlement, active authority" },
  { code: "NC/I", label: "NC/I - Name change / Reentitlement, inactive authority" },
  { code: "PFB", label: "PFB - Freight forwarder of property, including household goods" },
  { code: "PFO", label: "PFO - Freight forwarder of household goods" },
  { code: "PFP", label: "PFP - Freight forwarder of property (except household goods)" },
  { code: "PHO", label: "PHO - Contract carrier of household goods" },
  { code: "PMH", label: "PMH - Contract carrier of property, including household goods" },
  { code: "PMO", label: "PMO - Contract carrier of property (except household goods)" },
  { code: "PPO", label: "PPO - Contract carrier of passengers" },
  { code: "REI", label: "REI - Reinstatement of authority" },
];

export function WatchlistCreateModal({ open, onClose, workOrderId, workOrder }: WatchlistCreateModalProps) {
  const { toast } = useToast();
  const [identifierType, setIdentifierType] = useState<"mc" | "dot">("mc");
  const [mcNumber, setMcNumber] = useState("");
  const [dotNumber, setDotNumber] = useState("");
  const [docketPrefix, setDocketPrefix] = useState<string>("MC");
  const [docketSuffix, setDocketSuffix] = useState<string>("");
  const [letterType, setLetterType] = useState<string>("");
  const [authorityTypeCode, setAuthorityTypeCode] = useState<string>("");
  const [watchlistType, setWatchlistType] = useState<"single" | "multiple">("single");
  const [watchlistDays, setWatchlistDays] = useState("7");

  const { data: watchlistDefaults } = useQuery<Record<string, any>>({
    queryKey: ['/api/watchlist-defaults'],
  });

  const defaultDays = watchlistDefaults?.internal_watchlist_default_days != null
    ? String(watchlistDefaults.internal_watchlist_default_days)
    : "7";

  useEffect(() => {
    if (open) {
      setWatchlistDays(defaultDays);
    }
  }, [open, defaultDays]);

  useEffect(() => {
    if (open && workOrder) {
      const carrierDocketNumber = workOrder.carrier?.docketNumber;
      const carrierDocketPrefix = workOrder.carrier?.docketPrefix;
      const carrierDotNumber = workOrder.company?.dotNumber;
      
      if (carrierDocketNumber && carrierDocketPrefix) {
        setMcNumber(carrierDocketNumber.toString());
        setDocketPrefix(carrierDocketPrefix);
        setIdentifierType("mc");
      }
      
      if (carrierDotNumber) {
        setDotNumber(carrierDotNumber.toString());
        if (!carrierDocketNumber) {
          setIdentifierType("dot");
        }
      }
    }
  }, [open, workOrder]);

  const createWatchlistMutation = useMutation({
    mutationFn: async () => {
      const watchlistData: any = {
        externalReferenceId: workOrderId.toString(),
        watchlistDays: parseInt(watchlistDays),
        watchlistType: watchlistType,
      };

      if (identifierType === "mc") {
        if (!mcNumber.trim()) {
          throw new Error("MC Number is required");
        }
        watchlistData.targetType = "docket";
        watchlistData.docketPrefix = docketPrefix;
        watchlistData.docketNumber = parseInt(mcNumber);
        if (docketSuffix) {
          watchlistData.docketSuffix = docketSuffix;
        }
      } else if (identifierType === "dot") {
        if (!dotNumber.trim()) {
          throw new Error("DOT Number is required");
        }
        watchlistData.targetType = "dot";
        watchlistData.usdotNumber = parseInt(dotNumber);
      }

      if (letterType) {
        watchlistData.letterType = letterType;
      }
      
      if (authorityTypeCode) {
        watchlistData.authorityTypeCode = authorityTypeCode;
      }

      await apiRequest("POST", "/api/watchlists", watchlistData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/work-orders/${workOrderId}/watchlists`] });
      queryClient.invalidateQueries({ queryKey: ["/api/watchlists"] });
      toast({
        title: "Success",
        description: "Watchlist created successfully",
      });
      onClose();
      resetForm();
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create watchlist",
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setMcNumber("");
    setDotNumber("");
    setIdentifierType("mc");
    setDocketPrefix("MC");
    setDocketSuffix("");
    setLetterType("");
    setAuthorityTypeCode("");
    setWatchlistType("single");
    setWatchlistDays(defaultDays);
  };

  const handleSubmit = () => {
    const targetNumber = identifierType === "mc" ? mcNumber : dotNumber;
    
    if (!targetNumber.trim()) {
      toast({
        title: "Invalid Input",
        description: `Please enter a ${identifierType === "mc" ? "MC" : "DOT"} number`,
        variant: "destructive",
      });
      return;
    }

    if (!watchlistDays || parseInt(watchlistDays) <= 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter a valid number of days",
        variant: "destructive",
      });
      return;
    }

    createWatchlistMutation.mutate();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>Create Watchlist</DialogTitle>
          <DialogDescription>
            Monitor for new authority letters for a specific carrier or docket
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-180px)] pr-4">
          <div className="space-y-4">
            <div>
              <Label htmlFor="identifier-type">Identifier Type</Label>
              <Select 
                value={identifierType} 
                onValueChange={(value: "mc" | "dot") => setIdentifierType(value)}
              >
                <SelectTrigger data-testid="select-identifier-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mc">MC Number</SelectItem>
                  <SelectItem value="dot">DOT Number</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {identifierType === "mc" ? (
              <>
                <div>
                  <Label htmlFor="mc-number">MC Number <span className="text-red-500">*</span></Label>
                  <Input
                    id="mc-number"
                    placeholder="Enter MC number (e.g., 1116057)"
                    value={mcNumber}
                    onChange={(e) => setMcNumber(e.target.value.replace(/\D/g, ''))}
                    data-testid="input-mc-number"
                  />
                </div>

                <div>
                  <Label htmlFor="docket-prefix">Docket Prefix <span className="text-gray-500 text-xs">(Optional)</span></Label>
                  <Select 
                    value={docketPrefix} 
                    onValueChange={setDocketPrefix}
                  >
                    <SelectTrigger data-testid="select-docket-prefix">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="MC">MC</SelectItem>
                      <SelectItem value="FF">FF</SelectItem>
                      <SelectItem value="MX">MX</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="docket-suffix">Docket Suffix <span className="text-gray-500 text-xs">(Optional)</span></Label>
                  <Select 
                    value={docketSuffix || undefined} 
                    onValueChange={setDocketSuffix}
                  >
                    <SelectTrigger data-testid="select-docket-suffix">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="C">C</SelectItem>
                      <SelectItem value="P">P</SelectItem>
                      <SelectItem value="B">B</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <div>
                <Label htmlFor="dot-number">DOT Number <span className="text-red-500">*</span></Label>
                <Input
                  id="dot-number"
                  placeholder="Enter DOT number (e.g., 3896151)"
                  value={dotNumber}
                  onChange={(e) => setDotNumber(e.target.value.replace(/\D/g, ''))}
                  data-testid="input-dot-number"
                />
              </div>
            )}

            <div>
              <Label htmlFor="letter-type">Letter Type <span className="text-gray-500 text-xs">(Optional)</span></Label>
              <Select 
                value={letterType || undefined} 
                onValueChange={setLetterType}
              >
                <SelectTrigger data-testid="select-letter-type">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Authority Grant">Authority Grant</SelectItem>
                  <SelectItem value="Mexican Carrier">Mexican Carrier</SelectItem>
                  <SelectItem value="Name Change">Name Change</SelectItem>
                  <SelectItem value="Reinstatement">Reinstatement</SelectItem>
                  <SelectItem value="Transfer">Transfer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="authority-type">Authority Type <span className="text-gray-500 text-xs">(Optional)</span></Label>
              <Select 
                value={authorityTypeCode || undefined} 
                onValueChange={setAuthorityTypeCode}
              >
                <SelectTrigger data-testid="select-authority-type">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  {AUTHORITY_TYPES.map((type) => (
                    <SelectItem key={type.code} value={type.code}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="watchlist-type">Watchlist Type</Label>
              <Select 
                value={watchlistType} 
                onValueChange={(value: "single" | "multiple") => setWatchlistType(value)}
              >
                <SelectTrigger data-testid="select-watchlist-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Single Match (stops after first match)</SelectItem>
                  <SelectItem value="multiple">Continuous (monitors for entire duration)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="watchlist-days">Duration (days)</Label>
              <Input
                id="watchlist-days"
                type="number"
                min="1"
                max="365"
                placeholder="Enter number of days (1-365)"
                value={watchlistDays}
                onChange={(e) => setWatchlistDays(e.target.value)}
                data-testid="input-watchlist-days"
              />
              <p className="text-xs text-gray-500 mt-1">
                How long to monitor for new authority letters
              </p>
            </div>
          </div>
        </ScrollArea>

        <div className="flex gap-2 justify-end pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => {
              onClose();
              resetForm();
            }}
            data-testid="button-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createWatchlistMutation.isPending}
            data-testid="button-create-watchlist"
          >
            {createWatchlistMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Eye className="w-4 h-4 mr-2" />
                Create Watchlist
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
